import { Toaster as Sonner, toast } from "sonner";
import { useDashTheme } from "@/hooks/useDashTheme";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Premium sonner toaster, theme-aware.
 *
 * Top-right placement so toasts don't collide with the page-level CTA bar
 * at the bottom of dash-v2 pages. Mint accent, slim border, soft shadow,
 * tabular numerics where applicable. Success toasts feel like a stamp
 * landing; errors feel firm but not loud.
 *
 * Toasts portal into <body>, OUTSIDE the dash-v2 wrapper, so they can't
 * inherit the wrapper-scoped light-mode CSS — and the classNames below are
 * concrete colours, not tokens. We read the dashboard theme directly (the
 * same `cr.dash.theme` source of truth as the rest of the app) and swap to a
 * light palette so toasts match a light dashboard instead of staying dark.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useDashTheme();
  const isLight = theme === "light";

  const toastBase = isLight
    ? "group-[.toaster]:bg-white/95 group-[.toaster]:text-[#0f1414] group-[.toaster]:border group-[.toaster]:border-[#e4e6e6] group-[.toaster]:shadow-[0_8px_32px_rgba(15,20,20,0.12),0_0_0_1px_rgba(15,20,20,0.02)_inset]"
    : "group-[.toaster]:bg-[#0e1111]/95 group-[.toaster]:text-[#f5f5f5] group-[.toaster]:border group-[.toaster]:border-[#1c2020] group-[.toaster]:shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.02)_inset]";

  const descriptionColor = isLight ? "group-[.toast]:text-[#5a6363]" : "group-[.toast]:text-[#9ca3a3]";

  const actionButton = isLight
    ? "group-[.toast]:bg-[#137a43] group-[.toast]:text-white group-[.toast]:font-semibold group-[.toast]:rounded-md group-[.toast]:text-[12px]"
    : "group-[.toast]:bg-[#00e676] group-[.toast]:text-[#070808] group-[.toast]:font-semibold group-[.toast]:rounded-md group-[.toast]:text-[12px]";

  const cancelButton = isLight
    ? "group-[.toast]:bg-transparent group-[.toast]:text-[#5a6363] group-[.toast]:hover:text-[#0f1414]"
    : "group-[.toast]:bg-transparent group-[.toast]:text-[#9ca3a3] group-[.toast]:hover:text-[#f5f5f5]";

  const closeButton = isLight
    ? "group-[.toast]:bg-[#f4f5f5] group-[.toast]:border-[#e4e6e6] group-[.toast]:text-[#5a6363] group-[.toast]:hover:text-[#0f1414]"
    : "group-[.toast]:bg-[#0a0c0c] group-[.toast]:border-[#1c2020] group-[.toast]:text-[#9ca3a3] group-[.toast]:hover:text-[#f5f5f5]";

  return (
    <Sonner
      position="top-right"
      offset={20}
      gap={10}
      visibleToasts={4}
      closeButton
      style={{ zIndex: 9999, fontFamily: "'Inter Tight', system-ui, sans-serif" }}
      theme={isLight ? "light" : "dark"}
      duration={3500}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast: `group toast ${toastBase} group-[.toaster]:backdrop-blur-md group-[.toaster]:rounded-[12px] group-[.toaster]:px-4 group-[.toaster]:py-3 group-[.toaster]:text-[13px]`,
          title: "group-[.toast]:text-[13px] group-[.toast]:font-semibold",
          description: `${descriptionColor} group-[.toast]:text-[12px] group-[.toast]:mt-0.5`,
          actionButton,
          cancelButton,
          closeButton,
          success: "group-[.toaster]:!border-s-2 group-[.toaster]:!border-s-[#00b85f]",
          error: "group-[.toaster]:!border-s-2 group-[.toaster]:!border-s-[#ff5757]",
          warning: "group-[.toaster]:!border-s-2 group-[.toaster]:!border-s-[#ffb800]",
          info: "group-[.toaster]:!border-s-2 group-[.toaster]:!border-s-[#00bfff]",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
